import { create } from "zustand";
import { noteServerNow } from "../state/serverTime.js";
import { RealtimeClient, type Cursor, type RealtimeEvent, type RealtimeStatus } from "./realtime.js";
import { invalidateServerQueries } from "./query.js";
import { withPushPresentation } from "./pushContext.js";
import { onRequestScopeReset } from "./scope.js";

export type WsEvent = RealtimeEvent;
type Listener = (event: WsEvent) => void;
export const useWsStatus = create<{ status: RealtimeStatus; attempts: number }>(() => ({ status: "idle", attempts: 0 }));
const listeners = new Map<string, Set<Listener>>();
let resync: () => Promise<void> = async () => { throw new Error("Snapshot recovery is not configured"); };
export function onWs(type: string, listener: Listener): () => void {
  const set = listeners.get(type) ?? new Set(); set.add(listener); listeners.set(type, set);
  return () => { set.delete(listener); };
}
function dispatch(event: WsEvent): void {
  noteServerNow(event.serverNow);
  if (event.id > 0) invalidateServerQueries();
  withPushPresentation(event.notification !== false, () => {
    for (const listener of listeners.get(event.type) ?? []) listener(event);
    for (const listener of listeners.get("*") ?? []) listener(event);
  });
}
const client = new RealtimeClient({
  url: () => `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`,
  socket: (url) => new WebSocket(url), event: dispatch,
  status: (status, attempts) => useWsStatus.setState({ status, attempts }), resync: () => resync(),
});
export function configureWsRecovery(recover: () => Promise<void>): void { resync = recover; }
export function suspendWs(): void { client.suspend(); }
export function connectWs(accountId: string, cursor: Cursor): void { client.baseline(accountId, cursor); }
export function disconnectWs(): void { client.stop(); }
onRequestScopeReset(disconnectWs);
